// A closed loop pushed out from itself by a fixed distance (R548).
//
// Two chart pieces draw a line that runs BESIDE a body of rock and not on
// it. Card 26 puts the danger line 8 px outside a ridge, and card 19 lays a
// place name 13 or 26 px off the crest it names. The terrain plane cannot
// answer either one: its two distance channels are INSIDE distances, zero
// everywhere outside a body (`terrain-features.ts`), so a contour outside
// the rim has no field to follow.
//
// The traced outline does answer it. `hex-ring.ts` gives the hex-bound loops
// of any set of cells, and a loop offset along its own vertex normals is the
// same shape a stated distance further out.
//
// **THE SIGN IS THE LOOP'S OWN.** A caller must not have to know which way
// `traceHexLoops` wound the loop it is holding. A POSITIVE distance is
// therefore always OUTWARD and a negative one always inward, whatever the
// winding: the shoelace area says which way the loop turns, and the edge
// normal is taken with that sign.
//
// **A MITRE IS CAPPED.** At a sharp vertex the two edge normals nearly
// oppose one another, and the exact mitre runs away to infinity. The offset
// vertex is held to `MITRE_CAP` times the distance, which rounds such a
// corner off instead. A hex outline turns by 60 degrees at most, so the cap
// is never reached on the loops the chart hands it; it is here for a loop
// that came from somewhere else.

import { loopArea, type Loop, type Point } from './contours';

/** How far out a vertex may go, as a multiple of the offset distance. */
const MITRE_CAP = 2;

/** A loop of fresh points, so a caller may write into what it gets back. */
function copyLoop(loop: readonly Point[]): Loop {
  return loop.map((p) => [p[0], p[1]] as Point);
}

/**
 * A closed loop pushed `d` px along its own outward normal.
 *
 * A loop of under three points, or one whose area is zero, is handed back
 * unchanged: there is no inside for a normal to point away from. `d` of 0
 * is the loop itself.
 */
export function offsetLoop(loop: readonly Point[], d: number): Loop {
  const n = loop.length;
  if (n < 3 || d === 0) return copyLoop(loop);
  // Positive is counter-clockwise. Only the sign is read.
  const area = loopArea(loop);
  if (area === 0) return copyLoop(loop);
  // The outward normal of the edge a→b of a counter-clockwise loop is
  // (dy, −dx); a clockwise loop takes the other one.
  const sign = area > 0 ? 1 : -1;
  const nx: number[] = new Array(n);
  const ny: number[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const a = loop[i], b = loop[(i + 1) % n];
    const ex = b[0] - a[0], ey = b[1] - a[1];
    const len = Math.hypot(ex, ey);
    if (len === 0) { nx[i] = 0; ny[i] = 0; continue; }
    nx[i] = (sign * ey) / len;
    ny[i] = (-sign * ex) / len;
  }
  const out: Loop = [];
  const cap = MITRE_CAP * Math.abs(d);
  for (let i = 0; i < n; i++) {
    const prev = (i + n - 1) % n;
    let mx = nx[prev] + nx[i];
    let my = ny[prev] + ny[i];
    const m = Math.hypot(mx, my);
    if (m < 1e-9) {
      // The two edges double back on one another. The vertex has no mitre,
      // so it takes the outgoing edge's own normal.
      mx = nx[i]; my = ny[i];
    } else {
      // The mitre length is 1/cos(θ/2), which the half-sum's own length is
      // the reciprocal of: |n0 + n1| = 2·cos(θ/2).
      const k = 2 / (m * m);
      mx *= k; my *= k;
    }
    let ox = mx * d, oy = my * d;
    const len = Math.hypot(ox, oy);
    // `d` is not 0 here, so `cap` is above 0 and so is a `len` over it.
    if (len > cap) {
      ox = (ox / len) * cap;
      oy = (oy / len) * cap;
    }
    out.push([loop[i][0] + ox, loop[i][1] + oy]);
  }
  return out;
}

/**
 * A loop resampled so that no two kept points are nearer than `stepPx`.
 *
 * A marching-squares contour carries a point every texel, which is four
 * world px. A chart line drawn from every one of them is four times the
 * segments it needs and reads no differently. The first point is always
 * kept, and a loop that would collapse to under three points is handed back
 * unchanged, so a small body never loses its shape.
 */
export function thinLoop(loop: readonly Point[], stepPx: number): Loop {
  if (loop.length < 3 || stepPx <= 0) return copyLoop(loop);
  const out: Loop = [[loop[0][0], loop[0][1]]];
  let last = loop[0];
  for (let i = 1; i < loop.length; i++) {
    const p = loop[i];
    if (Math.hypot(p[0] - last[0], p[1] - last[1]) < stepPx) continue;
    out.push([p[0], p[1]]);
    last = p;
  }
  // The closing segment must not be shorter than the step either.
  if (out.length > 3) {
    const a = out[out.length - 1], b = out[0];
    if (Math.hypot(a[0] - b[0], a[1] - b[1]) < stepPx) out.pop();
  }
  return out.length >= 3 ? out : copyLoop(loop);
}
